/**
 * Embeds a Microsoft Copilot Studio agent via Bot Framework Web Chat (Direct Line).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Alert } from '@mui/material';
import ReactWebChat from 'botframework-webchat';
import { createStore } from 'botframework-webchat-core';
import { DirectLine, ConnectionStatus } from 'botframework-directlinejs';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import { useAuthStore } from '@store/auth.store';
import { subscribeCopilotSso } from '@utils/copilotSso';
import { copilotService, type CopilotHistoryMessage } from '@services/copilot.service';
import { useChatbotSuggestions } from '@hooks/useChatbotSuggestions';
import { ChatSuggestionPrompts } from './ChatSuggestionPrompts';
import { CopilotThinkingIndicator } from './CopilotThinkingIndicator';
import { CopilotConnectingState } from './CopilotConnectingState';
import { copilotWebChatStyleOptions } from './copilotWebChatStyleOptions';
import { COPILOT_CHAT_WAITING_CLASS } from './copilotWebChat.constants';
import {
  copilotConnectingShellSx,
  copilotErrorStateSx,
  copilotSuggestionsOverlaySx,
  copilotThinkingIndicatorSx,
  getCopilotChatRootSx,
} from './copilot.styles';
const INCOMING_ACTIVITY = 'DIRECT_LINE/INCOMING_ACTIVITY';

const toDirectLineActivity = (
  msg: CopilotHistoryMessage,
  conversationId: string,
  userId: string,
  userName: string
) => {
  const isUser = msg.role === 'user';
  return {
    type: 'message',
    id: msg.activity_id || `portal-${msg.id}`,
    timestamp: msg.created_at,
    channelId: 'directline',
    from: {
      id: isUser ? userId : 'copilot-bot',
      role: isUser ? 'user' : 'bot',
      name: isUser ? userName : 'Copilot',
    },
    text: msg.content,
    conversation: { id: conversationId },
  };
};

const BOT_RESPONSE_TIMEOUT_MS = 120_000;

export interface CopilotStudioChatProps {
  token: string;
  conversationId: string;
  directLineDomain: string;
  conversationKey?: string;
  historyMessages?: CopilotHistoryMessage[];
  onNewConversation?: () => void;
  loading?: boolean;
  error?: string | null;
  isPanelVisible?: boolean;
}

const normalizeDomain = (domain: string): string => domain.replace(/\/$/, '');

export const CopilotStudioChat: React.FC<CopilotStudioChatProps> = ({
  token,
  conversationId,
  directLineDomain,
  conversationKey,
  historyMessages = [],
  onNewConversation,
  loading,
  error,
  isPanelVisible,
}) => {
  const user = useAuthStore((state) => state.user);
  const userId = user?.email || user?.id || 'portal-user';
  const userName = user?.email || user?.name || 'Portal user';
  const { data: suggestions = [], isLoading: suggestionsLoading, isFetching: suggestionsFetching } =
    useChatbotSuggestions();
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [directLine, setDirectLine] = useState<InstanceType<typeof DirectLine> | null>(null);
  const [isWaitingForBot, setIsWaitingForBot] = useState(false);
  const [hasUserMessage, setHasUserMessage] = useState(() =>
    historyMessages.some((message) => message.role === 'user')
  );

  const directLineRef = useRef<{ sessionId: string; line: InstanceType<typeof DirectLine> } | null>(
    null
  );
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const waitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendBoxSyncTimersRef = useRef<number[]>([]);
  const hydratedActivityIdsRef = useRef<Set<string>>(new Set());
  const isWaitingForBotRef = useRef(false);

  const clearSendBoxSyncTimers = useCallback(() => {
    sendBoxSyncTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    sendBoxSyncTimersRef.current = [];
  }, []);

  const syncSendBoxDisabledState = useCallback((disabled: boolean) => {
    const container = chatContainerRef.current;
    if (!container) {
      return;
    }

    const textarea = container.querySelector<HTMLTextAreaElement>(
      '.webchat__send-box-text-box__input'
    );
    const htmlInput = container.querySelector<HTMLElement>(
      '.webchat__send-box-text-box__html-text[contenteditable]'
    );
    const sendButton = container.querySelector<HTMLButtonElement>('.webchat__send-box__button');

    if (textarea) {
      if (textarea.readOnly !== disabled) {
        textarea.readOnly = disabled;
      }
      if (textarea.disabled !== disabled) {
        textarea.disabled = disabled;
      }
      if (disabled && document.activeElement === textarea) {
        textarea.blur();
      }
    }

    if (htmlInput) {
      const editable = disabled ? 'false' : 'true';
      if (htmlInput.contentEditable !== editable) {
        htmlInput.contentEditable = editable;
      }
      if (disabled && document.activeElement === htmlInput) {
        htmlInput.blur();
      }
    }

    if (sendButton && sendButton.disabled !== disabled) {
      sendButton.disabled = disabled;
    }
  }, []);

  const scheduleSendBoxDisabledSync = useCallback(
    (disabled: boolean) => {
      syncSendBoxDisabledState(disabled);
      clearSendBoxSyncTimers();
      if (disabled) {
        sendBoxSyncTimersRef.current = [0, 50, 150].map((delay) =>
          window.setTimeout(() => syncSendBoxDisabledState(true), delay)
        );
      }
    },
    [clearSendBoxSyncTimers, syncSendBoxDisabledState]
  );

  const clearWaitingTimeout = useCallback(() => {
    if (waitingTimeoutRef.current) {
      clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
  }, []);

  const stopWaitingForBot = useCallback(() => {
    clearWaitingTimeout();
    clearSendBoxSyncTimers();
    isWaitingForBotRef.current = false;
    setIsWaitingForBot(false);
    scheduleSendBoxDisabledSync(false);
  }, [clearSendBoxSyncTimers, clearWaitingTimeout, scheduleSendBoxDisabledSync]);

  const startWaitingForBot = useCallback(() => {
    clearWaitingTimeout();
    isWaitingForBotRef.current = true;
    setIsWaitingForBot(true);
    scheduleSendBoxDisabledSync(true);
    waitingTimeoutRef.current = setTimeout(() => {
      stopWaitingForBot();
    }, BOT_RESPONSE_TIMEOUT_MS);
  }, [clearWaitingTimeout, scheduleSendBoxDisabledSync, stopWaitingForBot]);

  const attachWaitingHandlers = useCallback(
    (line: InstanceType<typeof DirectLine>) => {
      const originalPostActivity = line.postActivity.bind(line);
      line.postActivity = (activity) => {
        if (activity.type === 'message' && activity.text?.trim()) {
          if (isWaitingForBotRef.current) {
            return Observable.throw(new Error('Please wait for the assistant to respond.'));
          }

          // Mark that the user has spoken immediately, rather than waiting for
          // activity$ to echo this message back — that echo is not guaranteed, and
          // without it hasUserMessage never flips, so once the bot's reply arrives
          // and isWaitingForBot clears, the starter-prompt overlay wrongly reappears
          // on top of the actual reply.
          if (activity.from?.role === 'user') {
            setHasUserMessage(true);
          }

          startWaitingForBot();
          // Persist the outgoing message ourselves using the server-assigned id from the
          // POST response, rather than relying solely on activity$ echoing it back — that
          // echo is not guaranteed by every Direct Line channel/timing, which was silently
          // dropping the user's own messages from history while bot replies still saved fine.
          // Use .do() on the caller's subscription: postActivity is a cold observable, so a
          // separate .subscribe() here would POST the message a second time (duplicate bubble).
          const post$ = originalPostActivity(activity).do((id: string) => {
            if (id) {
              persistActivityRef.current({ ...activity, id });
            }
          });
          return post$.catch((err: unknown) => {
            stopWaitingForBot();
            return Observable.throw(err);
          });
        }
        return originalPostActivity(activity);
      };
    },
    [startWaitingForBot, stopWaitingForBot]
  );

  const isBotResponseActivity = useCallback(
    (activity: { from?: { role?: string }; type?: string; text?: string }) => {
      return activity.from?.role === 'bot' && activity.type === 'message';
    },
    []
  );

  const knownActivityIds = useMemo(
    () =>
      new Set(
        historyMessages
          .map((msg) => msg.activity_id)
          .filter((id): id is string => Boolean(id))
      ),
    [historyMessages]
  );

  const persistedOrInFlightActivityIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    persistedOrInFlightActivityIdsRef.current.clear();
  }, [conversationKey, conversationId]);

  const persistActivity = useCallback(
    (activity: { id?: string; type?: string; text?: string; from?: { role?: string } }) => {
      if (activity.type !== 'message' || !activity.text?.trim() || !activity.id || !conversationId) {
        return;
      }

      if (knownActivityIds.has(activity.id) || persistedOrInFlightActivityIdsRef.current.has(activity.id)) {
        return;
      }

      const fromRole = activity.from?.role;
      if (fromRole !== 'bot' && fromRole !== 'user') {
        return;
      }

      const role = fromRole === 'bot' ? 'assistant' : 'user';
      persistedOrInFlightActivityIdsRef.current.add(activity.id);

      void copilotService
        .saveMessage({
          conversation_id: conversationId,
          role,
          content: activity.text.trim(),
          activity_id: activity.id,
        })
        .catch((err: unknown) => {
          persistedOrInFlightActivityIdsRef.current.delete(activity.id!);
          if (import.meta.env.DEV) {
            console.warn('[Copilot] Failed to persist message:', err);
          }
        });
    },
    [conversationId, conversationKey, knownActivityIds]
  );

  const persistActivityRef = useRef(persistActivity);
  useEffect(() => {
    persistActivityRef.current = persistActivity;
  }, [persistActivity]);

  const hasValidSession = Boolean(token && conversationId);

  const webChatStore = useMemo(() => createStore(), [conversationKey]);

  useEffect(() => {
    hydratedActivityIdsRef.current.clear();
  }, [conversationKey, conversationId]);

  const isPanelVisibleRef = useRef(isPanelVisible);
  useEffect(() => {
    // Clear any unsent draft text when the panel is closed, so reopening starts with an empty send box.
    if (isPanelVisibleRef.current && !isPanelVisible) {
      webChatStore.dispatch({ type: 'WEB_CHAT/SET_SEND_BOX', payload: { text: '' } });
    }
    isPanelVisibleRef.current = isPanelVisible;
  }, [isPanelVisible, webChatStore]);

  const conversationIdentityRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const identity = `${conversationKey ?? ''}:${conversationId ?? ''}`;
    const isNewConversation = conversationIdentityRef.current !== identity;
    conversationIdentityRef.current = identity;

    const hasHistoryUserMessage = historyMessages.some((message) => message.role === 'user');

    // A mid-conversation history refetch (e.g. from ensureSession()) can race with
    // in-flight message saves and return an incomplete list. Only let it upgrade
    // hasUserMessage to true, never downgrade it back to false and resurrect the
    // starter-prompt overlay over an active conversation. Reset the baseline only
    // when the conversation itself actually changed (e.g. "+ New").
    setHasUserMessage((prev) => (isNewConversation ? hasHistoryUserMessage : prev || hasHistoryUserMessage));
  }, [conversationKey, conversationId, historyMessages]);

  useEffect(() => {
    return () => {
      clearSendBoxSyncTimers();
    };
  }, [clearSendBoxSyncTimers]);

  const hydrateHistory = useCallback(() => {
    if (!conversationId || historyMessages.length === 0) {
      return;
    }

    historyMessages.forEach((msg) => {
      const activityId = msg.activity_id || `portal-${msg.id}`;
      if (hydratedActivityIdsRef.current.has(activityId)) {
        return;
      }

      hydratedActivityIdsRef.current.add(activityId);

      try {
        webChatStore.dispatch({
          type: INCOMING_ACTIVITY,
          payload: {
            activity: toDirectLineActivity(msg, conversationId, userId, userName),
          },
        });
      } catch (err: unknown) {
        hydratedActivityIdsRef.current.delete(activityId);
        if (import.meta.env.DEV) {
          console.warn('[Copilot] Failed to hydrate message:', err);
        }
      }
    });
  }, [conversationId, historyMessages, userId, userName, webChatStore]);

  useEffect(() => {
    if (directLine) {
      hydrateHistory();
    }
  }, [directLine, historyMessages, hydrateHistory]);

  useEffect(() => {
    if (!hasValidSession) {
      directLineRef.current?.line.end();
      directLineRef.current = null;
      setDirectLine(null);
      return;
    }

    const sessionId = `${conversationKey ?? '0'}:${token}:${conversationId}`;
    setConnectionError(null);

    let line: InstanceType<typeof DirectLine>;
    if (directLineRef.current?.sessionId === sessionId) {
      line = directLineRef.current.line;
    } else {
      directLineRef.current?.line.end();
      directLineRef.current = null;
      // Backend already POST /conversations — reconnect via GET + polling.
      // Do NOT pass streamUrl: it is single-use and React Strict Mode remounts
      // would consume it, leaving Web Chat "online" but unable to send.
      line = new DirectLine({
        token,
        conversationId,
        domain: normalizeDomain(directLineDomain),
        webSocket: true,
      });
      attachWaitingHandlers(line);
      directLineRef.current = { sessionId, line };
      stopWaitingForBot();
    }

    setDirectLine(line);

    const unsubscribeSso = subscribeCopilotSso(line, userId, userName);

    const activitySub = line.activity$.subscribe((activity) => {
      if (
        activity.type === 'message' &&
        activity.text?.trim() &&
        activity.from?.role === 'user'
      ) {
        setHasUserMessage(true);
      }
      if (isBotResponseActivity(activity)) {
        stopWaitingForBot();
      }
      persistActivity(activity);
    });

    const statusSub = line.connectionStatus$.subscribe((status) => {
      if (import.meta.env.DEV) {
        console.log('[Copilot] Direct Line status:', ConnectionStatus[status]);
      }
      if (status === ConnectionStatus.Online) {
        hydrateHistory();
      }
      if (status === ConnectionStatus.FailedToConnect) {
        stopWaitingForBot();
        setConnectionError(
          'Could not connect to AI Assistant. Ensure the regional Direct Line URL matches your agent (EU → europe.directline.botframework.com).'
        );
      } else if (status === ConnectionStatus.ExpiredToken) {
        stopWaitingForBot();
        onNewConversation?.();
      }
    });

    return () => {
      unsubscribeSso();
      activitySub.unsubscribe();
      statusSub.unsubscribe();
    };
  }, [
    attachWaitingHandlers,
    conversationKey,
    conversationId,
    directLineDomain,
    hasValidSession,
    isBotResponseActivity,
    onNewConversation,
    hydrateHistory,
    persistActivity,
    stopWaitingForBot,
    token,
    userId,
    userName,
  ]);

  useEffect(() => {
    return () => {
      clearWaitingTimeout();
      directLineRef.current?.line.end();
      directLineRef.current = null;
    };
  }, [clearWaitingTimeout]);

  const handleSuggestionClick = useCallback(
    (query: string) => {
      const line = directLineRef.current?.line;
      if (!line || !query.trim()) {
        return;
      }

      setHasUserMessage(true);
      line
        .postActivity({
          from: { id: userId, role: 'user' },
          type: 'message',
          text: query.trim(),
        })
        .subscribe(
          () => undefined,
          (err: unknown) => {
            if (import.meta.env.DEV) {
              console.warn('[Copilot] Failed to send suggestion:', err);
            }
            stopWaitingForBot();
          }
        );
    },
    [stopWaitingForBot, userId]
  );

  const showSuggestions =
    !hasUserMessage &&
    !isWaitingForBot &&
    (suggestionsLoading || suggestionsFetching || suggestions.length > 0);

  const renderSuggestionPrompts = (variant: 'panel' | 'overlay') =>
    showSuggestions ? (
      <ChatSuggestionPrompts
        suggestions={suggestions}
        onSuggestionClick={handleSuggestionClick}
        isLoading={suggestionsLoading || suggestionsFetching}
        variant={variant}
      />
    ) : null;

  if (loading || !hasValidSession || !directLine) {
    return (
      <Box sx={copilotConnectingShellSx}>
        <CopilotConnectingState />
      </Box>
    );
  }

  const displayError = error || connectionError;

  if (displayError) {
    return (
      <Box sx={copilotErrorStateSx}>
        <Alert severity="error">
          {displayError}
        </Alert>
        {onNewConversation && (
          <Button variant="outlined" size="small" onClick={onNewConversation}>
            Retry
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={chatContainerRef}
      className={isWaitingForBot ? COPILOT_CHAT_WAITING_CLASS : undefined}
      sx={getCopilotChatRootSx(showSuggestions)}
    >
      <ReactWebChat
        key={`${conversationKey ?? 0}-${conversationId}`}
        directLine={directLine}
        store={webChatStore}
        userID={userId}
        username={userName}
        styleOptions={copilotWebChatStyleOptions}
        disabled={isWaitingForBot}
      />
      {showSuggestions && (
        <Box sx={copilotSuggestionsOverlaySx}>{renderSuggestionPrompts('overlay')}</Box>
      )}
      {isWaitingForBot && (
        <Box sx={copilotThinkingIndicatorSx}>
          <CopilotThinkingIndicator />
        </Box>
      )}
    </Box>
  );
};
